/**
 * Hinge compensation (spec 005; moved to Shared by spec 021 v13 so the store's
 * `setSpriteHinge` and the `editSprite.hinge` operation are one
 * implementation). Pure.
 */
import type { Hinge, Orientation, Position, ResponsiveLayout, Sprite, SpriteSize } from '../@types';
import { STAGE_SIZES } from '../runtime/evaluateFrame';
import { resolveSpriteSize } from './spriteSize';
import { getBasisOrientation, getResponsivePosition, getResponsiveTransform } from './responsiveLayout';

const ORIENTATIONS: readonly Orientation[] = ['portrait', 'landscape', 'desktop'];

/**
 * Hinge offset in canvas px when the hinge moves from `oldHinge` to `newHinge`
 * for a box of `size`, scaled and rotated: moving the position by this keeps
 * the rendered box where it is.
 */
function hingeShift(oldHinge: Hinge, newHinge: Hinge, size: SpriteSize, scale: number, rotation: number) {
  const dx = (newHinge.x - oldHinge.x) * size.width * scale;
  const dy = (newHinge.y - oldHinge.y) * size.height * scale;
  const rad = (rotation * Math.PI) / 180;
  return {
    x: dx * Math.cos(rad) - dy * Math.sin(rad),
    y: dx * Math.sin(rad) + dy * Math.cos(rad),
  };
}

/**
 * The sprite's layout with every explicit position compensated for a hinge
 * change, so the rendered box stays where it is in every orientation: the
 * basis orientation's position, and each non-basis orientation's overridden
 * fields, each with that orientation's size, scale and rotation. Fields that
 * follow the basis keep following it; a derived position (spec 021) shifts
 * and keeps its mark.
 */
export function compensateHinge(sprite: Sprite, oldHinge: Hinge, newHinge: Hinge): ResponsiveLayout {
  const layout = sprite.layout;
  const basis = getBasisOrientation(layout);
  const size = resolveSpriteSize(sprite);
  let next: ResponsiveLayout = { ...layout };

  for (const o of ORIENTATIONS) {
    const own = layout[o]?.position as Partial<Position> | undefined;
    if (!own) continue;
    const hasX = typeof own.x === 'number';
    const hasY = typeof own.y === 'number';
    if (!hasX && !hasY) continue;

    const pos = getResponsivePosition(layout, o);
    const t = getResponsiveTransform(layout, o, sprite.transform);
    const stage = STAGE_SIZES[o] ?? STAGE_SIZES.portrait;
    const shift = hingeShift(oldHinge, newHinge, size, t.scale ?? 1, t.rotation || 0);
    const dx = (shift.x / stage.width) * 100;
    const dy = (shift.y / stage.height) * 100;

    const isBasis = o === basis;
    const position = isBasis
      ? { ...pos, x: pos.x + dx, y: pos.y + dy }
      : {
          ...own,
          ...(hasX ? { x: pos.x + dx } : {}),
          ...(hasY ? { y: pos.y + dy } : {}),
        };
    next = { ...next, [o]: { ...layout[o], position } } as ResponsiveLayout;
  }
  return next;
}
